/**
 * Chart primitives specific to the Student & Parent portals, in the same
 * editorial light/dark language as charts.tsx (no chart library). Data marks use
 * the fixed mastery hues (teal / rose) that read on both grounds; text + grid use
 * Tailwind dark: variants so the second theme gets equal care.
 */

import { Check } from 'lucide-react';
import type { PaceStripDot, TrendPoint } from './roleProjections';
import { LOOP_STEPS } from './roleProjections';

const TEAL = '#2F8F80';
const ROSE = '#CB5B6B';

/** Each dot = one question, x = seconds spent, colour = right/wrong.
 *  A fast-and-wrong cluster on the left tells the "you rush when it's hard" story. */
export function PaceStrip({ dots }: { dots: PaceStripDot[] }) {
  const w = 320, h = 74, pad = 16, maxSec = 18;
  const xs = (s: number) => pad + (Math.min(s, maxSec) / maxSec) * (w - pad * 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label="Time spent per question">
      {[5, 10, 15].map((s) => (
        <g key={s}>
          <line x1={xs(s)} y1={12} x2={xs(s)} y2={h - 18} className="stroke-black/10 dark:stroke-white/10" strokeDasharray="2 3" />
          <text x={xs(s)} y={h - 4} textAnchor="middle" className="fill-[#8A8593] text-[8.5px]">{s}s</text>
        </g>
      ))}
      {dots.map((d, i) => (
        <circle key={i} cx={xs(d.sec)} cy={16 + (i % 3) * 15} r={4.5} fill={d.correct ? TEAL : ROSE} fillOpacity={d.correct ? 0.9 : 0.95} />
      ))}
    </svg>
  );
}

/** Cumulative concepts secure over dated attempts, with an emphasised endpoint. */
export function TrendLine({ points, max }: { points: TrendPoint[]; max: number }) {
  const w = 300, h = 104, pad = 12;
  if (points.length < 2) {
    return <div className="py-6 text-center text-[12.5px] text-[#8A8593]">Trend appears after two or more modules.</div>;
  }
  const xs = (i: number) => pad + i * ((w - pad * 2) / (points.length - 1));
  const ys = (v: number) => h - 20 - (v / max) * (h - 42);
  const line = points.map((p, i) => `${xs(i)},${ys(p.v)}`).join(' ');
  const area = `${xs(0)},${h - 16} ${line} ${xs(points.length - 1)},${h - 16}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label="Concepts secure over the term">
      <defs>
        <linearGradient id="pc-trend" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={TEAL} stopOpacity="0.26" />
          <stop offset="1" stopColor={TEAL} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#pc-trend)" />
      <polyline points={line} fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => {
        const last = i === points.length - 1;
        return (
          <g key={i}>
            <circle cx={xs(i)} cy={ys(p.v)} r={last ? 4.5 : 3} fill={last ? TEAL : 'var(--pc-node, #fff)'} stroke={TEAL} strokeWidth="2" className="[--pc-node:#fff] dark:[--pc-node:#14141d]" />
            <text x={xs(i)} y={ys(p.v) - 9} textAnchor="middle" className="fill-[#1F6B5F] text-[10.5px] font-bold dark:fill-[#5FC7B6]">{p.v}</text>
            <text x={i === 0 ? 2 : last ? w - 2 : xs(i)} y={h - 2} textAnchor={i === 0 ? 'start' : last ? 'end' : 'middle'} className="fill-[#8A8593] text-[9px] font-semibold">{p.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Syllabus coverage with an "expected by now" marker — a curriculum norm, not peers. */
export function CoverageBar({ done, total, expectedByNow }: { done: number; total: number; expectedByNow: number }) {
  const pct = (done / total) * 100;
  const exp = (expectedByNow / total) * 100;
  return (
    <div className="pt-[18px]">
      <div className="relative h-3.5 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: TEAL }} />
      </div>
      <div className="relative h-0">
        <div className="absolute top-[-19px] h-5 w-[2px] -translate-x-1/2 bg-[#6D28D9] dark:bg-[#B69BF5]" style={{ left: `${exp}%` }} />
        <div className="absolute top-[2px] -translate-x-1/2 whitespace-nowrap text-[9.5px] font-bold text-[#6D28D9] dark:text-[#B69BF5]" style={{ left: `${exp}%` }}>expected by now</div>
      </div>
    </div>
  );
}

/** Learn -> Test -> Review -> Retake, with the learner's current step marked. */
export function LoopStepper({ at }: { at: number }) {
  return (
    <div className="flex items-start justify-between gap-1">
      {LOOP_STEPS.map((s, i) => {
        const done = i < at;
        const cur = i === at;
        return (
          <div key={s} className="relative flex-1 text-center">
            {i < LOOP_STEPS.length - 1 && (
              <div className={`absolute left-1/2 top-[15px] h-[2px] w-full ${i < at ? 'bg-[#2F8F80]' : 'bg-black/10 dark:bg-white/10'}`} />
            )}
            <div
              className={`relative mx-auto grid h-[30px] w-[30px] place-items-center rounded-full border-2 text-[12px] font-bold ${
                done ? 'border-[#2F8F80] bg-[#2F8F80] text-white'
                  : cur ? 'border-[#6D28D9] bg-[#7C3AED]/10 text-[#6D28D9] dark:border-[#B69BF5] dark:text-[#B69BF5]'
                    : 'border-black/15 text-[#8A8593] dark:border-white/15'
              }`}
            >
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <div className={`mt-2 text-[10.5px] font-semibold leading-[1.28] ${cur ? 'text-[#1B1826] dark:text-white' : 'text-[#8A8593]'}`}>{s}</div>
          </div>
        );
      })}
    </div>
  );
}
